import React from "react";
import DOMPurify from "dompurify";
import {
  FaCalendarAlt,
  FaFileAlt,
  FaImage,
  FaPen,
  FaTimes,
} from "react-icons/fa";

import {
  formatDate,
  getArticleDate,
  getMediaUrl,
  getStatusClass,
  getStatusLabel,
  stripHtml,
} from "../utils/actualitesUtils";

function DetailItem({ label, value, icon: Icon }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-start gap-3">
        {Icon && (
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-white text-slate-500 dark:bg-slate-950 dark:text-slate-300">
            <Icon />
          </span>
        )}

        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
            {label}
          </p>
          <p className="mt-1 break-words text-sm font-semibold text-slate-800 dark:text-slate-100">
            {value || "—"}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function ActualiteDetails({ article, onEdit, onClose }) {
  const imageUrl = getMediaUrl(article.image_url);
  const hasContent = Boolean(stripHtml(article.content));

  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900">
        <div className="relative h-72 bg-slate-100 dark:bg-slate-800">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={article.title}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full flex-col items-center justify-center text-slate-400">
              <FaImage className="text-5xl" />
              <p className="mt-3 text-sm font-bold">Aucune image renseignée</p>
            </div>
          )}

          <div className="absolute left-5 top-5">
            <span
              className={`rounded-full px-3 py-1 text-xs font-bold ${getStatusClass(
                article.is_active
              )}`}
            >
              {getStatusLabel(article.is_active)}
            </span>
          </div>

          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/90 to-transparent p-5">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-orange-200">
              Actualité
            </p>
            <h3 className="mt-1 text-3xl font-bold text-white">
              {article.title}
            </h3>
          </div>
        </div>
      </section>

      <section>
        <h4 className="mb-3 text-sm font-bold uppercase tracking-[0.18em] text-slate-400">
          Informations
        </h4>

        <div className="grid gap-3 sm:grid-cols-2">
          <DetailItem
            label="Date de publication"
            value={formatDate(getArticleDate(article))}
            icon={FaCalendarAlt}
          />

          <DetailItem
            label="Statut"
            value={getStatusLabel(article.is_active)}
            icon={FaFileAlt}
          />

          <div className="sm:col-span-2">
            <DetailItem
              label="Image"
              value={article.image_url || "Non renseignée"}
              icon={FaImage}
            />
          </div>
        </div>
      </section>

      <section>
        <h4 className="mb-3 text-sm font-bold uppercase tracking-[0.18em] text-slate-400">
          Contenu
        </h4>

        <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-900">
          {hasContent ? (
            <div
              className="prose max-w-none prose-slate dark:prose-invert"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(article.content),
              }}
            />
          ) : (
            <div className="flex items-center gap-3 text-slate-500 dark:text-slate-400">
              <FaFileAlt />
              <p className="text-sm">Aucun contenu renseigné.</p>
            </div>
          )}
        </div>
      </section>

      <div className="sticky bottom-0 -mx-5 border-t border-slate-200 bg-white/90 px-5 py-4 backdrop-blur dark:border-slate-800 dark:bg-slate-950/90">
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaTimes />
            Fermer
          </button>

          <button
            type="button"
            onClick={onEdit}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-slate-950/20 transition hover:-translate-y-0.5 hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
          >
            <FaPen />
            Modifier
          </button>
        </div>
      </div>
    </div>
  );
}